import React, { useEffect, useRef, useState } from 'react';
import { DetailUser } from '@models/types/users.type';
import { Avatar } from 'geniebook-ui/dist';
import { AccountDropdown } from '@components/navigation/NavigationList';
import DropdownAccountNavigation, {
  DropdownNavigationItem,
} from '@components/navigation/DropdownAccountNavigation';
import BubbleDropdown from '@components/navigation/BubbleDropdown';
import AccountOverlay from '@components/account/AccountOverlay';
import Notification from '@components/navigation/notification/Notification';
import { NumberToFormattedString } from '@utils/helpers/formatNumber';
import { CDN_URL } from '@utils/constants/url.constants';
import { useIsJCUser, useUnreadNotificationCount } from '@hooks/user.hook';
import UserService from '@services/GeniebookAPI/UserServices.services';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useCanShowLeaderboard } from '@components/navigation/canShowLeaderboard';
import '../tour/Tour.css';
import moment from 'moment';
import Bell from '@assets/icons/bell';
import GenieaskNotificationIcon from '@pages/genieask/NotificationIcon';
import { useLocation } from 'react-router-dom';
import CircleInfoSolid from '@assets/icons/circle-info-solid';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { haveSkillLevelInfo } from '@atom/navbarAtom';
import { forceHideTimedWorksheetPopup } from '@atom/progress';
import GlobalStore from '@store/globalStore';
import { showGenieAskFloatingButtonAtom } from '@atom/worksheetAtom';
import ListIcon from '@assets/icons/list-solid';
import { showFilterSheetAtom } from '@atom/examResultAtom';
import GradeFilterSheet from './GradeFilterSheet';

export interface IBubbleTransaction {
  bubble_earned: number | null;
  bubble_spent: number | null;
}

export interface AccountSectionProps {
  userData: DetailUser | null;
  bubbleTransaction: IBubbleTransaction;
  logoutUser: () => void;
  showAccountDropdown: boolean;
  setShowAccountDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  isShowMobileBubbleDropdown: boolean;
  setIsShowMobileBubbleDropdown: React.Dispatch<React.SetStateAction<boolean>>;
}

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
const AccountSection = (props: AccountSectionProps) => {
  const location = useLocation();
  const [showBubbleDropdown, setShowBubbleDropdown] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showGradeFilterSheet, setShowGradeFilterSheet] = useState(false);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const [showGenieAskNotificationIcon] = useAtom(
    showGenieAskFloatingButtonAtom,
  );
  const width = useViewWidth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const showLeaderboard = useCanShowLeaderboard();
  const haveSkillLevelInfoState = useAtomValue(haveSkillLevelInfo, {
    store: GlobalStore,
  });
  const [showFilterExamResult, setShowFilterExamResult] =
    useAtom(showFilterSheetAtom);
  const { isJC } = useIsJCUser({ subjectId: undefined, generalPage: true });
  const store = useStore({ store: GlobalStore });

  const AccountDropdownProcessed: DropdownNavigationItem[] = AccountDropdown;
  for (let i = 0; i < AccountDropdownProcessed.length; i++) {
    if (AccountDropdownProcessed[i].key === 'badges') {
      AccountDropdownProcessed[i].hide = showLeaderboard;
    }
    if (AccountDropdownProcessed[i].key === 'achievements') {
      AccountDropdownProcessed[i].hide = !showLeaderboard;
    }
  }

  const unreadNotificationCount = useUnreadNotificationCount();
  useEffect(() => {
    if (
      !unreadNotificationCount.isLoading &&
      !unreadNotificationCount.data?.error &&
      unreadNotificationCount.data?.data
    ) {
      setNewNotificationCount(unreadNotificationCount.data.data);
    }
  }, [unreadNotificationCount.data]);

  async function triggerMarkReadAPI() {
    try {
      if (!newNotificationCount) {
        return;
      }
      const { data } = await UserService.markNotificationAsRead();
      if (!data.error) {
        setNewNotificationCount(0);
      }
    } catch (e) {
      /* empty */
    }
  }

  function handleAccountToggle(event: React.FormEvent) {
    event.stopPropagation();
    props.setShowAccountDropdown(!props.showAccountDropdown);
    setShowBubbleDropdown(false);
    setShowNotification(false);
  }

  function handleBubbleToggle(event: React.FormEvent) {
    event.stopPropagation();
    props.setShowAccountDropdown(false);
    setShowBubbleDropdown(!showBubbleDropdown);
    setShowNotification(false);
  }

  function handleNotificationToggle(event: React.FormEvent) {
    event.stopPropagation();
    setShowNotification(!showNotification);
    props.setShowAccountDropdown(false);
    setShowBubbleDropdown(false);
  }

  function handleClickItemDropdown(key: string) {
    if (key === 'logout') {
      props.logoutUser();
    }
  }

  return (
    <div className={'flex items-center lg:ml-auto h-8'}>
      {width >= breakpoint.lg ? (
        <div
          className={'flex-initial relative bubble-header pl-4'}
          data-testid={'bubble-container'}
        >
          <div
            className={
              'bg-primary-skyblue-light rounded-2xl py-2.5 pr-4 pl-8 cursor-pointer relative'
            }
            onClick={event => handleBubbleToggle(event)}
            data-testid={'bubble-section'}
          >
            <img
              src={`${CDN_URL}assets/icons/general/bubble.svg`}
              className={'absolute w-10 h-10 -top-0.5 -left-4'}
              alt={'bubble'}
            />
            <span
              className={
                'text-primary-navyblue-shade font-black text-md leading-4'
              }
              data-testid={'calculated-bubble'}
            >
              {NumberToFormattedString(
                (props.bubbleTransaction.bubble_earned ?? 0) -
                  (props.bubbleTransaction.bubble_spent ?? 0),
              )}
            </span>
          </div>
          {showBubbleDropdown ? (
            <div className={'absolute top-11 right-0'}>
              <BubbleDropdown
                earned={props.bubbleTransaction.bubble_earned ?? 0}
                spent={props.bubbleTransaction.bubble_spent ?? 0}
                closeDropdown={() => setShowBubbleDropdown(false)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* show skill level */}
      {haveSkillLevelInfoState && width < breakpoint.lg ? (
        <CircleInfoSolid
          className="w-6 fill-primary-apricot mr-3 cursor-pointer"
          onClick={() => {
            setShowGradeFilterSheet(!showGradeFilterSheet);
            store.set(forceHideTimedWorksheetPopup, !showGradeFilterSheet);
          }}
        />
      ) : null}

      {showGradeFilterSheet &&
        haveSkillLevelInfoState &&
        width < breakpoint.lg && (
          <GradeFilterSheet
            isOpen={showGradeFilterSheet}
            handleSheetState={setShowGradeFilterSheet}
          />
        )}

      {/* GenieAsk Chat icon */}
      {showGenieAskNotificationIcon && (
        <GenieaskNotificationIcon
          isActiveState={location.pathname.trim() == '/genieask'}
          headless={width < breakpoint.lg}
        />
      )}

      {width <= breakpoint.lg &&
        location.pathname.trim() === '/exam-result' && (
          <div className="flex mx-3">
            <ListIcon
              className="exclude-element text-primary-apricot cursor-pointer fill-current max-lg:flex max-lg:w-[22px]"
              onClick={() => setShowFilterExamResult(!showFilterExamResult)}
            />
          </div>
        )}

      <div className={'flex-initial relative lg:mx-6 cursor-pointer'}>
        <Bell
          className={
            'fill-current w-5 max-md:!w-[17.5px] text-primary-apricot hover:text-primary-skyblue ' +
            (showNotification ? '!text-primary-skyblue ' : '') +
            (newNotificationCount > 0 &&
              (width > breakpoint.md
                ? 'notification-jiggle-web'
                : 'notification-jiggle-mobile'))
          }
          onClick={event => handleNotificationToggle(event)}
          data-testid={'notification-button'}
        />
        {!!newNotificationCount && (
          <div
            className={
              'absolute -top-[5px] left-[10px] rounded-full text-center bg-[#E03636] w-auto min-w-[14px] h-3.5'
            }
            style={{ outline: '1px solid white' }}
            data-testid={'new-notification-tag'}
          >
            <span
              className={
                'text-neutral-chalk text-xs font-bold text-center !leading-3 ' +
                (newNotificationCount > 9 && 'px-1')
              }
            >
              {newNotificationCount > 9 ? '9+' : newNotificationCount}
            </span>
          </div>
        )}
        {showNotification ? (
          <div className={'absolute top-[40px] right-0'}>
            <Notification
              onClose={() => {
                setShowNotification(false);
              }}
              onOpen={() => {
                triggerMarkReadAPI();
              }}
            />
          </div>
        ) : null}
      </div>
      {width >= breakpoint.lg ? (
        <div className={'flex-initial relative cursor-pointer'}>
          <div ref={dropdownRef}>
            <Avatar
              onClick={event => handleAccountToggle(event)}
              src={props.userData?.pp + '?timestamp=' + moment().unix()}
              size={32}
              studentId={props?.userData?.user_id}
              alt={
                props.userData?.first_name
                  ? props.userData?.last_name
                    ? props.userData.first_name + ' ' + props.userData.last_name
                    : props.userData.first_name
                  : 'No Name'
              }
              data-testid={'picture avatar'}
            />
          </div>
          {props.showAccountDropdown ? (
            <div
              className={'absolute top-10 right-0'}
              data-testid={'account-dropdown-web'}
            >
              <DropdownAccountNavigation
                list={AccountDropdownProcessed}
                onClickItem={handleClickItemDropdown}
                closeDropdown={() => {
                  props.setShowAccountDropdown(false);
                }}
                name={
                  (props.userData?.first_name ?? '') +
                  ' ' +
                  (props.userData?.last_name ?? '')
                }
                userId={
                  props.userData?.user_id
                    ? parseInt(props.userData?.user_id)
                    : 0
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {props.showAccountDropdown && (
        <>
          <AccountOverlay
            bubbleEarned={props.bubbleTransaction.bubble_earned ?? 0}
            bubbleSpent={props.bubbleTransaction.bubble_spent ?? 0}
            isShowBubbleDropdown={props.isShowMobileBubbleDropdown}
            setIsShowBubbleDropdown={props.setIsShowMobileBubbleDropdown}
            setShowAccountDropdown={props.setShowAccountDropdown}
            showAccountDropdown={props.showAccountDropdown}
            logoutCallback={() => {
              handleClickItemDropdown('logout');
            }}
            isJC={isJC}
          />
        </>
      )}
    </div>
  );
};

export default AccountSection;
